import { Attr, BelongsTo, Model } from 'spraypaint';
import ApplicationRecord from '../ApplicationRecord';
import User from './User';

/**
 * One recorded acceptance of the current T&Cs by the logged-in user.
 *
 * Create-only and authenticated. The client says nothing but "yes": the user,
 * the version, the timestamp and the IP are all server-set, so there is no way
 * to accept on someone else's behalf, backdate a row, or name a version that
 * was never current. Registration records its own acceptance through
 * `POST /users/` instead; this is the path for accounts that already exist.
 *
 * Accepting twice is harmless - the table is an append-only audit log - so a
 * double-click needs no guarding.
 */
@Model()
export default class TermsAcceptance extends ApplicationRecord {
  static jsonapiType = 'terms-acceptances';

  /**
   * Record an acceptance and read back the user's refreshed status.
   *
   * `returnScope` puts `?include=user` on the POST, so the `201` sideloads the
   * user and the caller can clear the alert without a second request.
   */
  static async accept(): Promise<{ ok: boolean; user: User | null }> {
    const acceptance = new TermsAcceptance({ accepted: true });
    const ok = await acceptance.save({
      returnScope: TermsAcceptance.includes('user'),
    });
    return { ok, user: acceptance.user || null };
  }

  // Write-only: the affirmative itself. `false` is a validation error server
  // side, not a no-op, so there is nothing to send but `true`.
  @Attr() accepted: boolean;

  // Read-only audit fields, all server-set.
  @Attr() version: string;

  @Attr() acceptedAt: string;

  @Attr() ipAddress: string;

  @BelongsTo() user: User;
}
